import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { PopoverDirective } from 'ngx-bootstrap/popover';
@Component({
  selector: 'app-info-tooltip',
  templateUrl: './info-tooltip.component.html',
  styleUrls: ['./info-tooltip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // The popover body is an app-dialog, which closes whatever MatDialogRef it
  // can inject. When this trigger sits inside a Material dialog (Saved
  // Addresses on the checkout) the popover's embedded view resolves that
  // dialog's ref through this component's injector, so closing the popover
  // would close the dialog with it (#11618). Cutting the ref off here makes
  // the app-dialog's `@Optional()` injection resolve null, and its own guard
  // then leaves the host dialog alone.
  providers: [{ provide: MatDialogRef, useValue: null }],
})
export class InfoTooltipComponent implements OnDestroy {
  private static nextId = 0;

  /**
   * Unique id for the popover text, referenced by the trigger's
   * aria-describedby while the popover is open so screen readers announce the
   * revealed message (ngx-bootstrap's popover adds no such association itself).
   */
  readonly popoverTextId = `app-info-tooltip-text-${InfoTooltipComponent.nextId++}`;

  /** Message shown in the popover body. Pass it already translated. */
  @Input() text = '';

  @Input() heading?: string;
  @Input() icon = 'fa-info';

  /**
   * Accessible label for the trigger button. Falls back to a translated
   * "More information" in the template when not provided.
   */
  @Input() ariaLabel?: string;

  @ViewChild(PopoverDirective) private popover?: PopoverDirective;

  private readonly hideOnScroll = () => this.popover?.hide();

  /**
   * Escape closes the popover and nothing else. The CDK
   * OverlayKeyboardDispatcher listens for keydown on `document.body` and hands
   * it to the top-most overlay whatever the event target, so with the popover
   * open inside a Material dialog an unhandled Escape would close that dialog
   * (#11618). Registered in the capture phase on `document` so it runs before
   * the body listener, and only while the popover is open so Escape still
   * reaches the dialog once the popover is gone.
   */
  private readonly hideOnEscape = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || !this.popover?.isOpen) {
      return;
    }
    event.stopPropagation();
    this.popover.hide();
  };

  onShown(): void {
    document.addEventListener('scroll', this.hideOnScroll, true);
    document.addEventListener('keydown', this.hideOnEscape, true);
  }

  onHidden(): void {
    this.removeDocumentListeners();
  }

  ngOnDestroy(): void {
    this.removeDocumentListeners();
  }

  private removeDocumentListeners(): void {
    document.removeEventListener('scroll', this.hideOnScroll, true);
    document.removeEventListener('keydown', this.hideOnEscape, true);
  }
}
